import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { StyleService } from '@app/core/services/common/styles/style.service';
import { MixedObject } from '@core/interfaces/mixed-object';
import { BlockSettings, ViewPart } from '@core/models';
import { Block } from '@core/models/block/block.model';
import { GlobalService, MediaQuery } from '@core/services/common/global.service';
import { SubSink } from '@core/utils/subsink';
import { LightboxService } from '@modules/creator/components/special-block/lightbox/services/lightbox.service';
import { getStyles } from '@shared/helpers/block-helpers';
import { camelCase } from 'lodash';

@Component({
  selector: 'app-lightbox-basic-view',
  templateUrl: './lightbox-basic-view.component.html',
  styles: [`
    .thumbnail {
      width: auto;
      display: flex;
      flex-direction: column;
    }

    .thumbnail-wrapper {
      height: fit-content;

      > *[view-part="thumbnailBlock"] {
        margin: 0;
      }
    }

    .thumbnail,
    .photo {
      cursor: pointer;
    }
  `],
  standalone: false,
})
export class LightboxBasicViewComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() settings: BlockSettings;
  @Input() block: Block;

  viewPart = ViewPart;
  emptyContainer: Block;
  mainPhotoContainer: Block;
  photoContainer: Block;
  thumbnailsContainer: Block;
  thumbnailsSubContainer: Block;

  descrptionsEnabled = false;

  thumbnailBlockStyles: MixedObject = {};
  mainPhotoIndex = 0;
  private subSink = new SubSink();

  constructor(private lightBoxService: LightboxService,
              private globalService: GlobalService, private styleService: StyleService, private elRef: ElementRef) {
  }

  get thumbnailStyle() {
    return {
      ...this.thumbnailBlockStyles?.basic,
      overflow: undefined,
      height: undefined,
    };
  }

  /**
   * Klasy, po których reguła bloku faktycznie łapie element. MediaQueryBuilder buduje dla bloku
   * z `modelId` selektor złożony `.block_<uId>.m<modelId>`, a klasę `m<modelId>` dokłada
   * BlockComponent — czyli tylko elementom z `app-block`. Zwykły div-opakowanie musi dostać obie
   * klasy z szablonu, inaczej rozjeżdżają się na dwa elementy i reguła nie łapie się na nic.
   */
  blockClasses(block: Block): string[] {
    return block.modelId ? [block.uId, `m${block.modelId}`] : [block.uId];
  }

  getThumbnailImageBlock(children: Block[]): Block {
    return children.find(item => item.settings.viewPart === ViewPart.THUMBNAIL_BLOCK);
  }

  ngOnInit(): void {
    this.emptyContainer = this.block.children.find(item => item.settings.viewPart === ViewPart.EMPTY_CONTAINER);
    this.mainPhotoContainer = this.block.children.find(item => item.settings.viewPart === ViewPart.MAIN_PHOTO_CONTAINER);
    this.photoContainer = this.mainPhotoContainer.children.find(item => item.settings.viewPart === ViewPart.PHOTO_CONTAINER);
    this.descrptionsEnabled = !!this.photoContainer.descrptionsEnabled;

    this.thumbnailsContainer = this.block.children.find(item => item.settings.viewPart === ViewPart.THUMBNAILS_CONTAINER);
    if (this.thumbnailsContainer) {
      this.thumbnailsSubContainer = this.thumbnailsContainer.children
        .find(item => item.settings.viewPart === ViewPart.THUMBNAILS_SUB_CONTAINER);
    }
    this.subSink.sink = this.globalService.mediaQuery.subscribe((mediaQuery) => {
      this.generateBlockStyles(mediaQuery);
    });
    this.generateBlockStyles(this.globalService.mediaQuery.value);
  }


  ngAfterViewInit(): void {
    this.generateBlockStyles(this.globalService.mediaQuery.value);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  openFullScreenPreview(index: number) {
    this.lightBoxService.openFullScreen(index);
  }

  previewInMainPhoto(index: number) {
    this.mainPhotoIndex = index;
  }

  private generateBlockStyles(mediaQuery: MediaQuery): void {
    if (!this.thumbnailsSubContainer?.descrptionsEnabled) {
      return;
    }
    const querySnakeCase = camelCase(mediaQuery.query);
    if (this.thumbnailsSubContainer?.children.length > 0) {
      const thumbnailBlock = this.getThumbnailImageBlock(this.thumbnailsSubContainer?.children[0]?.children);
      if (thumbnailBlock) {
        this.thumbnailBlockStyles = getStyles(thumbnailBlock.styles, querySnakeCase);
      }
    }
  }
}
